/**
 * @fileoverview PDF文本层高亮业务逻辑钩子
 * 提供PDF文本层内高亮显示和交互的核心业务逻辑
 *
 * 这个钩子是PDF高亮功能的核心，对应text的useTranslatedWordHighlighter，
 * 但专门为PDF的word-based结构设计，直接在文本层内实现高亮。
 */
import { useState, useCallback, useMemo } from 'react';
import { buildPdfHighlightData } from '@readsavor/utils';
// --- Helper Functions ---
/**
 * Finds the appropriate highlight info based on character position within a word.
 *
 * @param highlights Array of highlight info for a word
 * @param characterPosition Position within the word (0-based)
 * @returns The matching highlight info or the first one if no specific match
 */
function findHighlightByPosition(highlights, characterPosition) {
    if (highlights.length === 0)
        return null;
    if (highlights.length === 1)
        return highlights[0];
    // If no position provided, return the first highlight
    if (characterPosition === undefined)
        return highlights[0];
    // Find all highlights that contain the character position
    const containingHighlights = [];
    for (const highlight of highlights) {
        if (highlight.isFullyHighlighted) {
            containingHighlights.push(highlight);
        }
        else if (highlight.startOffset !== undefined && highlight.endOffset !== undefined) {
            if (characterPosition >= highlight.startOffset && characterPosition <= highlight.endOffset) {
                containingHighlights.push(highlight);
            }
        }
    }
    if (containingHighlights.length === 0) {
        return highlights[0]; // Fallback to first highlight
    }
    if (containingHighlights.length === 1) {
        return containingHighlights[0];
    }
    // Multiple highlights contain this position - choose the smallest range (same logic as text)
    return containingHighlights.reduce((smallest, current) => {
        const getRange = (h) => {
            if (h.isFullyHighlighted) {
                // Full word highlights are considered to have the largest possible range
                return Number.MAX_SAFE_INTEGER;
            }
            return (h.endOffset || 0) - (h.startOffset || 0);
        };
        const rangeSmallest = getRange(smallest);
        const rangeCurrent = getRange(current);
        return rangeCurrent < rangeSmallest ? current : smallest;
    });
}
/**
 * Converts a backend TextSelectionTranslation object into a UI-friendly
 * UiHighlightTooltipData object.
 *
 * This function is extracted and simplified from the text highlighter version.
 * @param translation The translation object from the database.
 * @returns A tooltip data object, or undefined if the input is invalid.
 */
function convertTranslationToTooltipData(translation) {
    if (!translation || typeof translation.orderId !== 'number')
        return undefined;
    let displayTranslatedText = '';
    if (typeof translation.translatedText === 'string') {
        displayTranslatedText = translation.translatedText;
    }
    else if (translation.translatedText &&
        typeof translation.translatedText.translatedText === 'string') {
        displayTranslatedText = translation.translatedText.translatedText;
    }
    else {
        displayTranslatedText = 'Translation not available';
    }
    return {
        id: translation.orderId,
        originalText: translation.originalText,
        displayTranslatedText: displayTranslatedText,
        sourceLanguage: translation.sourceLanguage,
        targetLanguage: translation.targetLanguage,
        provider: translation.provider,
        model: translation.model,
    };
}
// --- The Hook ---
/**
 * A core business logic hook that calculates word-level highlight information for PDF text layers.
 *
 * This hook processes translation data and generates a mapping of word IDs to highlight information,
 * allowing the text layer to apply highlighting directly to word elements.
 *
 * @param params - The input data required for calculation.
 * @returns An object containing word highlight mappings and interaction handlers.
 */
export function usePdfTextLayerHighlight(params) {
    const { pageSpecificTranslations, paragraphsForPage, isLoading } = params;
    const [hoveredWord, setHoveredWord] = useState(null);
    const wordHighlights = useMemo(() => {
        // 1. Guard clauses
        if (isLoading || !pageSpecificTranslations || pageSpecificTranslations.length === 0) {
            return new Map();
        }
        // 2. Create word text length lookup function
        const getWordTextLength = (wordId) => {
            for (const paragraph of paragraphsForPage) {
                const word = paragraph.words.find(w => w.id === wordId);
                if (word)
                    return word.text.length;
            }
            return undefined;
        };
        // 3. Create paragraph words lookup function for multi-word highlighting
        const getParagraphWords = (paragraphId) => {
            const paragraph = paragraphsForPage.find(p => p.paragraphId === paragraphId);
            return paragraph?.words.map(w => ({ id: w.id }));
        };
        // 4. Use the existing buildPdfHighlightData utility with word order support
        const highlightMap = buildPdfHighlightData(pageSpecificTranslations, getWordTextLength, getParagraphWords);
        return highlightMap;
    }, [pageSpecificTranslations, paragraphsForPage, isLoading]);
    const handleWordHover = useCallback((wordId, element, characterPosition) => {
        const highlightInfoArray = wordHighlights.get(wordId);
        if (highlightInfoArray && highlightInfoArray.length > 0) {
            const targetHighlight = findHighlightByPosition(highlightInfoArray, characterPosition);
            if (targetHighlight) {
                const tooltipData = convertTranslationToTooltipData(targetHighlight.translationData);
                if (tooltipData) {
                    setHoveredWord({
                        ...tooltipData,
                        element,
                        highlightId: targetHighlight.id,
                        characterRange: targetHighlight.startOffset !== undefined && targetHighlight.endOffset !== undefined
                            ? [targetHighlight.startOffset, targetHighlight.endOffset]
                            : undefined,
                    });
                }
            }
        }
    }, [wordHighlights]);
    const handleWordLeave = useCallback(() => {
        setHoveredWord(null);
    }, []);
    return {
        wordHighlights,
        hoveredWord,
        loading: !!isLoading,
        handleWordHover,
        handleWordLeave,
    };
}
//# sourceMappingURL=usePdfTextLayerHighlight.js.map